import { addDays, isoWeekday } from '@selflevel/domain';

/**
 * EL CALENDARIO DEL DETALLE DE UN HÁBITO, como modelo.
 *
 * Una fila por semana y una columna por día —lunes primero—, para que encima se
 * pueda poner «L M X J V S D» y se lea como lo que es: un calendario. El mapa
 * de antes eran columnas de siete días contadas desde hace 83, así que la
 * primera fila era el día de la semana que tocara y no había forma de ponerle
 * cabecera.
 *
 * La última fila es la semana de hoy. Lo que queda de ella se marca como
 * futuro: se dibuja, para que la rejilla no quede coja, pero no se puede
 * marcar —rellenar hacia atrás es corregir el registro; hacia delante sería
 * inventarlo—.
 */
export type CeldaCalendario = {
  readonly localDate: string;
  readonly futuro: boolean;
  /**
   * El mes, abreviado, en el día 1 y en la primera celda —la que abre el
   * calendario, que casi nunca es día 1—; `null` en las demás. Sin esto la
   * rejilla pasaba del 31 al 1 sin decir de qué mes era ninguno de los dos.
   */
  readonly mes: string | null;
};

const MESES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
] as const;

function mesCorto(localDate: string): string {
  return MESES[Number(localDate.slice(5, 7)) - 1] ?? '';
}

export function semanasHasta(today: string, semanas: number): CeldaCalendario[][] {
  const lunesDeHoy = addDays(today, -(isoWeekday(today) - 1));
  const inicio = addDays(lunesDeHoy, -(semanas - 1) * 7);

  const filas: CeldaCalendario[][] = [];
  for (let s = 0; s < semanas; s++) {
    const fila: CeldaCalendario[] = [];
    for (let d = 0; d < 7; d++) {
      const localDate = addDays(inicio, s * 7 + d);
      const primera = s === 0 && d === 0;
      const mes = primera || localDate.endsWith('-01') ? mesCorto(localDate) : null;
      fila.push({ localDate, futuro: localDate > today, mes });
    }
    filas.push(fila);
  }
  return filas;
}

/** El estado para leerlo en voz alta. Decía el valor interno: «done». */
export function estadoLegible(status: string | undefined): string {
  if (status === 'done') return 'hecho';
  if (status === 'skipped') return 'omitido';
  return 'sin registrar';
}

/**
 * De dónde vino un registro, para enseñarlo. Lo propio no se etiqueta: es lo
 * normal. Lo de fuera siempre, porque el usuario tiene que poder ver qué marcó él
 * y qué marcó otra app en su nombre (PRD 14.5).
 */
export function origenLegible(source: string | undefined): string | null {
  if (!source || source === 'propio') return null;
  return source.startsWith('api:') ? source.slice('api:'.length) : source;
}
